import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { matchesApi, matchChatApi } from '../../lib/api';
import { toast } from 'sonner';
import {
  Calendar,
  Shield,
  Clock,
  Goal,
  AlertTriangle,
  Plus,
  X,
  Ban,
  Camera,
  Sparkles,
  Pencil,
  Zap,
  MessageSquare,
  LifeBuoy,
} from 'lucide-react';
import MatchChatDialog from './MatchChatDialog';
import ClubNameLabel from '../../components/ClubNameLabel';
import { MatchShareButton } from '../../components/MatchShareCard';
import { EAVerifyButton } from '../../components/EAVerifyButton';

const MatchPhotos = ({ matchId, isItalian, onLightbox }) => {
  const [photos, setPhotos] = useState(null);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const loadPhotos = async () => {
    if (photos !== null) { setExpanded(!expanded); return; }
    setLoadingPhotos(true);
    try {
      const res = await matchesApi.getPhotos(matchId);
      setPhotos(res.data?.photos || []);
      setExpanded(true);
    } catch { setPhotos([]); }
    setLoadingPhotos(false);
  };

  return (
    <div className="mt-3 pt-3 border-t border-white/10">
      <button
        onClick={loadPhotos}
        className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#00F0FF] transition-colors uppercase tracking-wider"
        data-testid={`load-photos-${matchId}`}
      >
        <Camera size={12} />
        {loadingPhotos ? (isItalian ? 'Caricamento...' : 'Loading...') :
          expanded ? (isItalian ? 'Nascondi Foto' : 'Hide Photos') :
          (isItalian ? 'Mostra Foto Partita' : 'Show Match Photos')}
      </button>
      {expanded && photos && photos.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1 mt-2">
          {photos.map((photo, idx) => (
            <img
              key={idx}
              src={photo}
              alt={`${isItalian ? 'Foto' : 'Photo'} ${idx + 1}`}
              className="w-24 h-24 object-cover rounded border border-white/10 cursor-pointer hover:border-[#00F0FF]/50 transition-colors flex-shrink-0"
              onClick={() => onLightbox(photo)}
              data-testid={`match-photo-${matchId}-${idx}`}
            />
          ))}
        </div>
      )}
      {expanded && photos && photos.length === 0 && (
        <p className="text-xs text-slate-600 mt-1">{isItalian ? 'Nessuna foto' : 'No photos'}</p>
      )}
    </div>
  );
};

const MatchCard = ({
  match,
  showReport = false,
  clubs,
  user,
  isSuperAdmin,
  isCaptain,
  userClubId,
  reportsLocked,
  isItalian,
  cancellingReport,
  onOpenReport,
  onEditReport,
  onCancelReport,
  onRequestJolly,
  onResolveDispute,
  onReportSynced,
  onLightbox,
  onClubClick,
  isTournament = false,
  champNames = {},
}) => {
  const getClub = (clubId) => clubs.find((c) => c.club_id === clubId);
  const homeClub = getClub(match.home_club_id);
  const awayClub = getClub(match.away_club_id);
  const isCompleted = match.status === 'completed';
  const isPending = match.status === 'pending_report';
  const isDisputed = match.status === 'disputed';

  const userReports = match.reports || [];

  // Derive the user's captain/vice club. Only captains and vice-captains can
  // submit match reports. We look for the club where this user is explicitly
  // captain_id or vice_captain_id (avoids stale user.club_id after transfers).
  const captainClub = clubs.find(c => c.captain_id === user?.user_id || c.vice_captain_id === user?.user_id || c.vice_captain_id_2 === user?.user_id);
  const userClubIdEffective = captainClub?.club_id || userClubId || user?.club_id;

  const userClubAlreadyReported = userReports.some(r => r.club_id === userClubIdEffective);

  const isClubCaptain = !!captainClub;
  const isViceCaptain = [captainClub?.vice_captain_id, captainClub?.vice_captain_id_2].includes(user?.user_id);

  // Match chat: capitani + vice di entrambe le squadre + super admin
  const [chatOpen, setChatOpen] = useState(false);
  const [requestingHelp, setRequestingHelp] = useState(false);
  const isInvolvedClub = userClubIdEffective && (
    match.home_club_id === userClubIdEffective || match.away_club_id === userClubIdEffective
  );
  const canOpenChat = isSuperAdmin || ((isClubCaptain || isViceCaptain) && isInvolvedClub);

  // Admin help: visible on every match card that is not completed
  // (campionati e tornei). Allowed roles: super admin (può convocare gli altri
  // admin) e capitani/vice delle 2 squadre coinvolte.
  const canRequestAdminHelp =
    !isCompleted &&
    (isSuperAdmin || ((isClubCaptain || isViceCaptain) && isInvolvedClub));

  const handleRequestAdminHelp = async () => {
    if (requestingHelp) return;
    const confirmMsg = isItalian
      ? 'Vuoi richiedere l\'intervento di un admin per questa partita? Tutti gli admin EPT verranno notificati ed entreranno nella chat partita.'
      : 'Request admin mediation for this match? All EPT admins will be notified and will join the match chat.';
    if (!window.confirm(confirmMsg)) return;
    setRequestingHelp(true);
    try {
      await matchChatApi.requestAdminHelp(match.match_id);
      toast.success(isItalian ? 'Admin notificati. Apriamo la chat partita.' : 'Admins notified. Opening match chat.');
      setChatOpen(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || (isItalian ? 'Errore nella richiesta admin' : 'Failed to request admin help'));
    } finally {
      setRequestingHelp(false);
    }
  };
  const canReport =
    (isSuperAdmin || isCaptain || isClubCaptain || isViceCaptain) &&
    (isSuperAdmin || match.home_club_id === userClubIdEffective || match.away_club_id === userClubIdEffective) &&
    !isCompleted &&
    !isDisputed &&
    !userClubAlreadyReported &&
    !reportsLocked;

  const canRequestJolly =
    !isTournament &&
    (isSuperAdmin || isCaptain || isClubCaptain || isViceCaptain) &&
    (!isSuperAdmin ? (match.home_club_id === userClubIdEffective || match.away_club_id === userClubIdEffective) : true) &&
    !isCompleted;

  const getStatusBadge = () => {
    if (isCompleted && match.forfeit) return { text: isItalian ? 'Tavolino' : 'Forfeit', cls: 'bg-orange-500/20 text-orange-400 border border-orange-500/30' };
    if (isCompleted) return { text: isItalian ? 'Completata' : 'Completed', cls: 'bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/30' };
    if (isDisputed) return { text: isItalian ? 'Contestata' : 'Disputed', cls: 'bg-[#FF1744]/20 text-[#FF1744] border border-[#FF1744]/30' };
    if (isPending) return { text: isItalian ? '1 Report' : '1 Report', cls: 'bg-[#FF9100]/20 text-[#FF9100] border border-[#FF9100]/30' };
    return { text: isItalian ? 'Programmata' : 'Scheduled', cls: 'bg-[#FFEA00]/20 text-[#FFEA00] border border-[#FFEA00]/30' };
  };
  const statusBadge = getStatusBadge();

  return (
    <div
      className="bg-[#0a0d15] border border-white/10  p-4 hover:border-[#00F0FF]/30 transition-colors"
      data-testid={`match-card-${match.match_id}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3 text-xs sm:text-sm">
          {match.match_date ? (
            <>
              <span className="flex items-center gap-1 sm:gap-1.5 text-slate-400">
                <Calendar size={14} className="text-[#0EA5E9] flex-shrink-0" />
                <span className="hidden sm:inline">{new Date(match.match_date).toLocaleDateString('it-IT', {
                  weekday: 'short', day: '2-digit', month: 'short', year: 'numeric'
                })}</span>
                <span className="sm:hidden">{new Date(match.match_date).toLocaleDateString('it-IT', {
                  day: '2-digit', month: 'short'
                })}</span>
              </span>
              <span className="flex items-center gap-1 sm:gap-1.5">
                <Clock size={14} className="text-[#FFEA00] flex-shrink-0" />
                <span className="text-[#FFEA00] font-mono font-bold">
                  {new Date(match.match_date).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </span>
            </>
          ) : (
            <span className="flex items-center gap-1.5 text-slate-500 italic">
              <Calendar size={14} />
              {isItalian ? 'Da programmare' : 'To be scheduled'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {(user?.role === 'super_admin' || user?.role === 'admin') && match.home_score !== null && match.home_score !== undefined && (
            <EAVerifyButton match={match} isItalian={isItalian} />
          )}
          {isCompleted && (
            <MatchShareButton
              match={match}
              homeClub={homeClub}
              awayClub={awayClub}
              championshipName={champNames[match.championship_id]}
              isItalian={isItalian}
            />
          )}
          <span
            className={`px-2 py-1 text-xs rounded uppercase tracking-wider ${statusBadge.cls}`}
            data-testid={`match-status-${match.match_id}`}
          >
            {statusBadge.text}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between">
        {/* Home Team */}
        <div className="flex-1 text-center min-w-0 cursor-pointer group/home" onClick={() => onClubClick && onClubClick(homeClub)} data-testid={`club-click-home-${match.match_id}`}>
          <div
            className={`w-10 h-10 sm:w-12 sm:h-12 bg-[#050508] border mx-auto mb-2 flex items-center justify-center overflow-hidden transition-all ${isCompleted && match.home_score > match.away_score ? 'border-[#00FF87]/40' : 'border-white/10'}`}
            style={{ 
              backgroundColor: homeClub?.colors?.primary,
              boxShadow: isCompleted && match.home_score > match.away_score ? '0 0 16px rgba(0,255,135,0.2)' : 'none'
            }}
          >
            {homeClub?.logo ? (
              <img src={homeClub.logo} alt={homeClub.name} className="w-full h-full object-cover" loading="lazy" />
            ) : (
              <Shield size={24} className="text-white/80" />
            )}
          </div>
          <ClubNameLabel
            as="p"
            name={homeClub?.name || 'TBD'}
            className={`font-medium text-xs sm:text-sm ${isCompleted && match.home_score > match.away_score ? 'text-[#00FF87]' : 'text-white'}`}
          />
          {isCompleted && match.home_score > match.away_score && (
            <span className="inline-block mt-1 px-2 py-0.5 text-[9px] uppercase tracking-widest bg-[#00FF87]/10 text-[#00FF87] border border-[#00FF87]/20 font-bold" data-testid="winner-badge-home">WINNER</span>
          )}
        </div>

        {/* Score */}
        <div className="px-3 sm:px-6 text-center flex-shrink-0">
          {isCompleted ? (
            <div className="font-mono text-2xl font-bold text-white relative">
              <span className={match.home_score > match.away_score ? 'text-[#00FF87]' : match.home_score < match.away_score ? 'text-slate-500' : ''} style={match.home_score > match.away_score ? {textShadow: '0 0 12px rgba(0,255,135,0.5)'} : {}}>
                {match.home_score}
              </span>
              <span className="text-slate-600 mx-1">-</span>
              <span className={match.away_score > match.home_score ? 'text-[#00FF87]' : match.away_score < match.home_score ? 'text-slate-500' : ''} style={match.away_score > match.home_score ? {textShadow: '0 0 12px rgba(0,255,135,0.5)'} : {}}>
                {match.away_score}
              </span>
            </div>
          ) : (
            <div className="font-mono text-xl font-bold text-slate-500">VS</div>
          )}
        </div>

        {/* Away Team */}
        <div className="flex-1 text-center min-w-0 cursor-pointer group/away" onClick={() => onClubClick && onClubClick(awayClub)} data-testid={`club-click-away-${match.match_id}`}>
          <div
            className={`w-10 h-10 sm:w-12 sm:h-12 bg-[#050508] border mx-auto mb-2 flex items-center justify-center overflow-hidden transition-all ${isCompleted && match.away_score > match.home_score ? 'border-[#00FF87]/40' : 'border-white/10'}`}
            style={{ 
              backgroundColor: awayClub?.colors?.primary,
              boxShadow: isCompleted && match.away_score > match.home_score ? '0 0 16px rgba(0,255,135,0.2)' : 'none'
            }}
          >
            {awayClub?.logo ? (
              <img src={awayClub.logo} alt={awayClub.name} className="w-full h-full object-cover" loading="lazy" />
            ) : (
              <Shield size={24} className="text-white/80" />
            )}
          </div>
          <ClubNameLabel
            as="p"
            name={awayClub?.name || 'TBD'}
            className={`font-medium text-xs sm:text-sm ${isCompleted && match.away_score > match.home_score ? 'text-[#00FF87]' : 'text-white'}`}
          />
          {isCompleted && match.away_score > match.home_score && (
            <span className="inline-block mt-1 px-2 py-0.5 text-[9px] uppercase tracking-widest bg-[#00FF87]/10 text-[#00FF87] border border-[#00FF87]/20 font-bold" data-testid="winner-badge-away">WINNER</span>
          )}
        </div>
      </div>

      {/* Match Events */}
      {isCompleted && match.events?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="flex flex-wrap gap-2">
            {match.events.map((event, idx) => (
              <span
                key={idx}
                className={`px-2 py-1 text-xs rounded flex items-center gap-1 ${
                  event.event_type === 'goal'
                    ? 'bg-[#00E676]/20 text-[#00E676]'
                    : event.event_type === 'assist'
                    ? 'bg-[#00F0FF]/20 text-[#00F0FF]'
                    : event.event_type === 'yellow_card'
                    ? 'bg-[#FFEA00]/20 text-[#FFEA00]'
                    : 'bg-[#FF1744]/20 text-[#FF1744]'
                }`}
                data-testid={`event-badge-${idx}`}
              >
                {event.event_type === 'goal' && <Goal size={12} />}
                {event.event_type === 'assist' && <Plus size={12} />}
                {event.event_type === 'yellow_card' && <AlertTriangle size={12} />}
                {event.event_type === 'red_card' && <Ban size={12} />}
                {event.player_name && <span className="font-medium">{event.player_name}</span>}
                {event.minute}'
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Match Photos */}
      {isCompleted && <MatchPhotos matchId={match.match_id} isItalian={isItalian} onLightbox={onLightbox} />}

      {/* Dual-Report Status Info */}
      {(isPending || isDisputed) && (
        <div className="mt-3 pt-3 border-t border-white/10">
          {isPending && (() => {
            const firstReport = userReports[0];
            const firstClub = firstReport ? getClub(firstReport.club_id) : null;
            const firstClubName = firstClub?.name || '?';
            // Detect a stuck match: 2 reports already saved but status didn't
            // transition to `completed` (auto-complete was interrupted by a
            // network failure on the second submission). Offer a retry button
            // to re-run the comparison.
            const isStuck = userReports.length >= 2;
            return (
              <div className="space-y-2">
                {isStuck ? (
                  <div className="flex flex-col gap-2 text-xs text-[#FF9100] bg-[#FF9100]/5 border border-[#FF9100]/20 rounded p-2" data-testid={`stuck-status-${match.match_id}`}>
                    <div className="flex items-center gap-2">
                      <AlertTriangle size={14} className="flex-shrink-0" />
                      <span>
                        {isItalian
                          ? 'Entrambi i club hanno già inviato il report, ma la verifica si è bloccata. Riprova a sincronizzare.'
                          : 'Both clubs already submitted reports, but verification stalled. Tap retry to resync.'}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      className="w-full bg-[#00F0FF]/15 border border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/25"
                      onClick={async () => {
                        try {
                          const res = await matchesApi.resync(match.match_id);
                          if (typeof onReportSynced === 'function') onReportSynced(res.data);
                        } catch (err) {
                          if (typeof onReportSynced === 'function') onReportSynced({ error: err?.response?.data?.detail || 'error' });
                        }
                      }}
                      data-testid={`resync-match-${match.match_id}`}
                    >
                      <span className="flex items-center gap-2 justify-center">
                        <Zap size={14} />
                        {isItalian ? 'Ri-verifica Report' : 'Retry Verification'}
                      </span>
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-xs text-[#FF9100] bg-[#FF9100]/5 border border-[#FF9100]/20 rounded p-2" data-testid={`pending-status-${match.match_id}`}>
                    <Clock size={14} className="flex-shrink-0" />
                    <span>
                      {isItalian
                        ? <><strong>{firstClubName}</strong> ha inviato il report. In attesa del report di <strong>{firstReport?.club_id === match.home_club_id ? (getClub(match.away_club_id)?.name || '?') : (getClub(match.home_club_id)?.name || '?')}</strong>.</>
                        : <><strong>{firstClubName}</strong> submitted report. Waiting for <strong>{firstReport?.club_id === match.home_club_id ? (getClub(match.away_club_id)?.name || '?') : (getClub(match.home_club_id)?.name || '?')}</strong>.</>
                      }
                    </span>
                  </div>
                )}
                {!userClubAlreadyReported && !isSuperAdmin && !isStuck && (
                  <div className="flex items-center gap-2 text-xs text-[#FFEA00]">
                    <AlertTriangle size={14} />
                    {isItalian ? 'Invia il tuo report per confermare il risultato!' : 'Submit your report to confirm the result!'}
                  </div>
                )}
              </div>
            );
          })()}
          {isDisputed && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-[#FF1744]" data-testid={`disputed-status-${match.match_id}`}>
                <AlertTriangle size={14} />
                {isItalian ? 'I risultati non corrispondono! In attesa di intervento admin.' : 'Results don\'t match! Awaiting admin resolution.'}
              </div>
              {userReports.length === 2 && (
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {userReports.map((r, idx) => {
                    const rClub = getClub(r.club_id);
                    return (
                      <div key={idx} className="bg-[#050508] border border-white/10 rounded p-2 text-center" data-testid={`dispute-report-${idx}`}>
                        <p className="text-slate-500 text-[10px] mb-1">{rClub?.name || '?'}</p>
                        <p className="font-mono font-bold text-white">{r.home_score} - {r.away_score}</p>
                      </div>
                    );
                  })}
                </div>
              )}
              {isSuperAdmin && (
                <Button
                  size="sm"
                  className="w-full bg-[#FF1744]/20 border border-[#FF1744]/30 text-[#FF1744] hover:bg-[#FF1744]/30"
                  onClick={() => onResolveDispute(match)}
                  data-testid={`resolve-dispute-${match.match_id}`}
                >
                  {isItalian ? 'Risolvi Contestazione' : 'Resolve Dispute'}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Chat capitani button */}
      {canOpenChat && !isCompleted && (
        <Button
          className="w-full mt-3 bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF] hover:bg-[#00F0FF]/20 transition-colors"
          onClick={() => setChatOpen(true)}
          data-testid={`match-chat-btn-${match.match_id}`}
        >
          <span className="flex items-center gap-2 justify-center">
            <MessageSquare size={16} />
            {isItalian ? 'Chat Capitani' : 'Captains Chat'}
          </span>
        </Button>
      )}

      {/* Chiedi intervento admin (tutte le partite non completate, capitani coinvolti) */}
      {canRequestAdminHelp && (
        <Button
          className="w-full mt-3 bg-[#FF00E5]/10 border border-[#FF00E5]/40 text-[#FF00E5] hover:bg-[#FF00E5]/20 transition-colors disabled:opacity-50"
          onClick={handleRequestAdminHelp}
          disabled={requestingHelp}
          data-testid={`request-admin-help-btn-${match.match_id}`}
        >
          <span className="flex items-center gap-2 justify-center">
            {requestingHelp ? (
              <span className="animate-spin w-4 h-4 border-2 border-[#FF00E5] border-t-transparent rounded-full" />
            ) : (
              <LifeBuoy size={16} />
            )}
            {requestingHelp
              ? (isItalian ? 'Invio richiesta...' : 'Requesting...')
              : (isItalian ? 'Chiedi aiuto a un admin' : 'Ask admin for help')}
          </span>
        </Button>
      )}

      {/* Jolly Button */}
      {!isCompleted && !isDisputed && canRequestJolly && (
        <Button
          className="w-full mt-3 bg-[#FFEA00]/10 border border-[#FFEA00]/30 text-[#FFEA00] hover:bg-[#FFEA00]/20 transition-colors"
          onClick={() => onRequestJolly(match)}
          data-testid={`jolly-btn-${match.match_id}`}
        >
          <span className="flex items-center gap-2 justify-center">
            <Sparkles size={16} />
            {isItalian ? 'Usa Jolly (Sposta Partita)' : 'Use Jolly (Reschedule)'}
          </span>
        </Button>
      )}

      {/* Report Button */}
      {canReport && showReport && (
        <Button
          className="w-full mt-4 btn-primary"
          onClick={() => onOpenReport(match)}
          data-testid={`submit-report-${match.match_id}`}
        >
          {isPending
            ? (isItalian ? 'Invia il Tuo Report' : 'Submit Your Report')
            : (isItalian ? 'Invia Report' : 'Submit Report')
          }
        </Button>
      )}

      {/* Edit & Cancel Report Buttons — Super Admin only */}
      {isCompleted && isSuperAdmin && (
        <div className="flex gap-2 mt-4">
          <Button
            className="flex-1 bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF] hover:bg-[#00F0FF]/20 transition-colors"
            onClick={() => onEditReport(match)}
            data-testid={`edit-report-${match.match_id}`}
          >
            <span className="flex items-center gap-2 justify-center">
              <Pencil size={16} />
              {isItalian ? 'Modifica Report' : 'Edit Report'}
            </span>
          </Button>
          <Button
            className="flex-1 bg-red-600/20 border border-red-500/30 text-red-400 hover:bg-red-600/40 hover:text-red-300 transition-colors"
            onClick={() => onCancelReport(match.match_id)}
            disabled={cancellingReport === match.match_id}
            data-testid={`cancel-report-${match.match_id}`}
          >
            {cancellingReport === match.match_id ? (
              <span className="flex items-center gap-2 justify-center">
                <span className="animate-spin w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full" />
                {isItalian ? 'Annullamento...' : 'Cancelling...'}
              </span>
            ) : (
              <span className="flex items-center gap-2 justify-center">
                <X size={16} />
                {isItalian ? 'Annulla Report' : 'Cancel Report'}
              </span>
            )}
          </Button>
        </div>
      )}
      {(isPending || isDisputed) && isSuperAdmin && (
        <Button
          className="w-full mt-4 bg-red-600/20 border border-red-500/30 text-red-400 hover:bg-red-600/40 hover:text-red-300 transition-colors"
          onClick={() => onCancelReport(match.match_id)}
          disabled={cancellingReport === match.match_id}
          data-testid={`cancel-report-${match.match_id}`}
        >
          {cancellingReport === match.match_id ? (
            <span className="flex items-center gap-2 justify-center">
              <span className="animate-spin w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full" />
              {isItalian ? 'Annullamento...' : 'Cancelling...'}
            </span>
          ) : (
            <span className="flex items-center gap-2 justify-center">
              <X size={16} />
              {isItalian ? 'Annulla Report' : 'Cancel Report'}
            </span>
          )}
        </Button>
      )}

      {/* Match chat dialog */}
      {canOpenChat && (
        <MatchChatDialog
          open={chatOpen}
          onOpenChange={setChatOpen}
          matchId={match.match_id}
          isItalian={isItalian}
          currentUserId={user?.user_id}
        />
      )}
    </div>
  );
};

export default MatchCard;
